import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import AuthLayout from '../components/layout/AuthLayout';
import './Auth.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');
  const [isLoading, setIsLoading] = useState(true);
  const [showManualConfig, setShowManualConfig] = useState(false);
  const [manualServerUrl, setManualServerUrl] = useState('');
  const [debugInfo, setDebugInfo] = useState([]);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await authService.verifyEmail(token);
        console.log('Email verification response:', response);
        
        // Handle different response formats
        if (response.status === true || response.success === true) {
          setStatus('success');
          setMessage(response.message || 'Email verified successfully! Redirecting to dashboard...');
          // Store token and user if provided in response
          if (response.token) {
            localStorage.setItem('authToken', response.token);
            if (response.user) {
              localStorage.setItem('user', JSON.stringify(response.user));
            }
            // User is now logged in with token, redirect to dashboard
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            // No token provided, redirect to login
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          }
        } else if (response.status === false || response.success === false) {
          setStatus('error');
          setMessage(response.message || 'Email verification failed. Please try again.');
        } else {
          // Fallback for other response formats
          setStatus('success');
          setMessage(response.message || 'Email verified successfully! Redirecting to dashboard...');
          // Store token and user if provided in response
          if (response.token) {
            localStorage.setItem('authToken', response.token);
            if (response.user) {
              localStorage.setItem('user', JSON.stringify(response.user));
            }
            // User is now logged in with token, redirect to dashboard
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            // No token provided, redirect to login
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        
        // Add debug information
        const debugEntry = {
          timestamp: new Date().toLocaleTimeString(),
          error: error.message,
          type: 'Auto Verification Failed'
        };
        setDebugInfo(prev => [...prev, debugEntry]);
        
        // Show detailed error message
        if (error.message.includes('Failed to connect to server')) {
          setMessage('Cannot connect to server. Please check if your server is running or try manual configuration.');
          setShowManualConfig(true);
        } else {
          setMessage(
            error.response?.data?.message ||
            error.message ||
            'Email verification failed. The link may be expired or invalid.'
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleManualVerification = async () => {
    if (!manualServerUrl.trim()) {
      alert('Please enter a server URL');
      return;
    }

    const token = searchParams.get('token');
    if (!token) {
      setMessage('No verification token found in URL');
      return;
    }

    setIsLoading(true);
    setStatus('verifying');
    setMessage('Trying manual verification...');

    try {
      const url = `${manualServerUrl.replace(/\/$/, '')}/auth/verify-email?token=${token}`;
      
      const debugEntry = {
        timestamp: new Date().toLocaleTimeString(),
        url: url,
        type: 'Manual Verification Attempt'
      };
      setDebugInfo(prev => [...prev, debugEntry]);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Manual verification result:', result);

      if (result.status === true || result.success === true) {
        setStatus('success');
        setMessage(result.message || 'Email verified successfully! Redirecting to dashboard...');
        // Store token and user if provided in response
        if (result.token) {
          localStorage.setItem('authToken', result.token);
          if (result.user) {
            localStorage.setItem('user', JSON.stringify(result.user));
          }
          // User is now logged in with token, redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          // No token provided, redirect to login
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        setStatus('error');
        setMessage(result.message || 'Email verification failed.');
      }
    } catch (error) {
      console.error('Manual verification error:', error);
      setStatus('error');
      setMessage(`Manual verification failed: ${error.message}`);
      
      const debugEntry = {
        timestamp: new Date().toLocaleTimeString(),
        error: error.message,
        type: 'Manual Verification Failed'
      };
      setDebugInfo(prev => [...prev, debugEntry]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = localStorage.getItem('pendingEmail');
    if (!email) {
      setMessage('No email found for verification. Please register again.');
      return;
    }

    try {
      setIsLoading(true);
      await authService.resendVerification(email);
      setMessage('Verification email has been resent. Please check your email.');
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
        'Failed to resend verification email. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="verification-spinner">
            <div className="spinner"></div>
          </div>
        );
      case 'success':
        return (
          <div className="verification-icon success">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#10B981"/>
              <path d="m9 12 2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="verification-icon error">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#EF4444"/>
              <path d="m15 9-6 6m0-6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AuthLayout>
      <div className="auth-container">
        <div className="auth-card">
          <div className="verification-content">
            {getStatusIcon()}

            <h2 className="auth-title">
              {status === 'verifying' && 'Verifying Email'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </h2>

            <p className="verification-message">{message}</p>

            {status === 'success' && (
              <div className="verification-success">
                <p className="redirect-message">
                  Redirecting to dashboard in 2 seconds...
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn btn-primary"
                >
                  Go to Dashboard
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className="verification-actions">
                {showManualConfig && (
                  <div className="manual-config" style={{ marginBottom: '16px', width: '100%' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#333' }}>Manual Server Configuration</h4>
                    <input
                      type="text"
                      placeholder="Enter your server URL (e.g., http://localhost:3001)"
                      value={manualServerUrl}
                      onChange={(e) => setManualServerUrl(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={handleManualVerification}
                      disabled={isLoading}
                      className="btn btn-primary"
                      style={{ width: '100%', marginBottom: '8px' }}
                    >
                      {isLoading ? 'Verifying...' : 'Try Manual Verification'}
                    </button>
                  </div>
                )}
                
                <button
                  onClick={handleResendVerification}
                  disabled={isLoading}
                  className="btn btn-outline"
                >
                  {isLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
                
                {!showManualConfig && (
                  <button
                    onClick={() => setShowManualConfig(true)}
                    className="btn btn-link"
                  >
                    Manual Configuration
                  </button>
                )}
                
                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-link"
                >
                  Back to Login
                </button>
              </div>
            )}

            {/* Debug Information */}
            {debugInfo.length > 0 && (
              <div className="debug-info" style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: '#f8f9fa', 
                borderRadius: '4px',
                fontSize: '12px',
                maxWidth: '100%',
                overflow: 'auto'
              }}>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>Debug Information:</h5>
                {debugInfo.map((info, index) => (
                  <div key={index} style={{ marginBottom: '4px', color: '#555' }}>
                    <strong>{info.timestamp}</strong> - {info.type}: {info.error || info.url}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default EmailVerification;